// Component: AlgoMiniChart.jsx
// Purpose: SVG sparkline/equity curve for algo strategy cards and list items

import React, { useMemo } from 'react';

//! Stable gradient ID derived from props — no Math.random() to avoid SVG repaint on every render
function AlgoMiniChart({ pnl = 0, heightClass = 'h-20', strategyId = 'default', tradePnls = null }) {
  const gradientId = `algo-grad-${strategyId}`;

  /*>>> EQUITY CURVE — computed from real trade P&L series <<<*/
  const equityCurve = useMemo(() => {
    if (!tradePnls || tradePnls.length < 2) return null;

    //--> Build cumulative P&L starting from zero
    const cumulative = [0];
    for (const p of tradePnls) cumulative.push(cumulative[cumulative.length - 1] + p);

    //--> Normalize to SVG viewBox 100 x 40 (y=2 = top, y=38 = bottom, 2px padding each side)
    const minY = Math.min(...cumulative);
    const maxY = Math.max(...cumulative);
    const yRange = maxY - minY;

    const toSvgY = (v) => {
      if (yRange === 0) return 20; // flat line at midpoint
      return 38 - ((v - minY) / yRange) * 36; // map [minY..maxY] → [38..2]
    };

    const xStep = 100 / (cumulative.length - 1);
    const points = cumulative
      .map((v, i) => `${(i * xStep).toFixed(2)},${toSvgY(v).toFixed(2)}`)
      .join(' ');

    //! Color determined by final cumulative value vs zero
    const finalPnl = cumulative[cumulative.length - 1];
    const color = finalPnl >= 0 ? '#00C292' : '#E46A76';

    return { points, color };
  }, [tradePnls]);

  //--> Flat line for zero P&L (no trades yet) — only shown when no real curve is available
  if (pnl === 0 && !equityCurve) {
    return (
      <div className={`${heightClass} w-full relative flex items-center justify-center bg-bg-3/30 rounded border border-dashed border-[var(--divider)]`}>
        <p className="text-[10px] text-fg-3">No data yet</p>
      </div>
    );
  }

  //--> If we have real equity data, render polyline; otherwise fall back to static trend path
  if (equityCurve) {
    const { points, color } = equityCurve;
    //* Close the fill area by appending bottom-right and bottom-left corners
    const lastPoint = points.split(' ').pop();
    const lastX = parseFloat(lastPoint.split(',')[0]);
    const fillPoints = `${points} ${lastX.toFixed(2)},40 0,40`;

    return (
      <div className={`w-full relative ${heightClass}`}>
        <svg className="w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 40">
          <defs>
            <linearGradient id={gradientId} x1="0%" x2="0%" y1="0%" y2="100%">
              <stop offset="0%" style={{ stopColor: color, stopOpacity: 0.2 }} />
              <stop offset="100%" style={{ stopColor: color, stopOpacity: 0 }} />
            </linearGradient>
          </defs>
          {/* --> Gradient fill below the equity curve */}
          <polygon points={fillPoints} fill={`url(#${gradientId})`} />
          {/* --> Actual equity curve polyline */}
          <polyline
            points={points}
            fill="none"
            stroke={color}
            strokeWidth="1.5"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </div>
    );
  }

  //--> Static fallback: trend path based on P&L direction
  const isPositive = pnl > 0;
  const color = isPositive ? '#00C292' : '#E46A76';

  const pathD = isPositive
    ? 'M0 35 C 10 32, 20 38, 30 25 C 40 15, 50 20, 60 15 C 70 10, 80 12, 90 5 L 100 2'
    : 'M0 10 C 20 15, 40 10, 60 25 C 70 30, 80 35, 100 38 L 100 40';

  return (
    <div className={`w-full relative ${heightClass}`}>
      <svg className="w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 40">
        <defs>
          <linearGradient id={gradientId} x1="0%" x2="0%" y1="0%" y2="100%">
            <stop offset="0%" style={{ stopColor: color, stopOpacity: 0.2 }} />
            <stop offset="100%" style={{ stopColor: color, stopOpacity: 0 }} />
          </linearGradient>
        </defs>
        <path d={`${pathD} L 100 40 L 0 40 Z`} fill={`url(#${gradientId})`} />
        <path d={pathD} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
}

export default AlgoMiniChart;
